import { useState, useEffect, useCallback } from 'react';

const useMobileOptimization = () => {
  const [isMobile, setIsMobile] = useState(false);
  const [isTablet, setIsTablet] = useState(false);
  const [isDesktop, setIsDesktop] = useState(false);
  const [isSmallMobile, setIsSmallMobile] = useState(false);
  const [isLargeMobile, setIsLargeMobile] = useState(false);
  const [orientation, setOrientation] = useState('portrait');
  const [touchDevice, setTouchDevice] = useState(false);
  const [screenSize, setScreenSize] = useState({ width: 0, height: 0 });

  const checkDevice = useCallback(() => {
    const width = window.innerWidth;
    const height = window.innerHeight;
    
    setScreenSize({ width, height });
    setIsSmallMobile(width < 375);
    setIsMobile(width < 768);
    setIsLargeMobile(width >= 375 && width < 768);
    setIsTablet(width >= 768 && width < 1024);
    setIsDesktop(width >= 1024);
    
    setOrientation(width > height ? 'landscape' : 'portrait');
    
    // Check if device supports touch
    setTouchDevice('ontouchstart' in window || navigator.maxTouchPoints > 0);
  }, []);

  useEffect(() => {
    checkDevice();
    
    let timeoutId;
    const debouncedCheckDevice = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(checkDevice, 100);
    };

    window.addEventListener('resize', debouncedCheckDevice);
    window.addEventListener('orientationchange', debouncedCheckDevice);

    return () => {
      window.removeEventListener('resize', debouncedCheckDevice);
      window.removeEventListener('orientationchange', debouncedCheckDevice);
      clearTimeout(timeoutId);
    };
  }, [checkDevice]);

  // Mobile-specific utilities
  const mobileUtils = {
    // Prevent zoom on input focus (iOS)
    preventZoom: (element) => {
      if (isMobile && element) {
        element.style.fontSize = '16px';
        element.setAttribute('autocomplete', 'off');
        element.setAttribute('autocorrect', 'off');
        element.setAttribute('autocapitalize', 'off');
        element.setAttribute('spellcheck', 'false');
      }
    },
    
    // Handle mobile scroll
    enableSmoothScroll: () => {
      if (isMobile) {
        document.documentElement.style.scrollBehavior = 'smooth';
        document.documentElement.style.webkitOverflowScrolling = 'touch';
      }
    },
    
    // Disable body scroll (for modals/sidebars)
    disableBodyScroll: () => {
      if (isMobile || isTablet) {
        const scrollY = window.scrollY;
        document.body.style.position = 'fixed';
        document.body.style.top = `-${scrollY}px`;
        document.body.style.width = '100%';
        document.body.style.overflow = 'hidden';
        document.body.dataset.scrollY = scrollY;
      }
    },
    
    // Enable body scroll
    enableBodyScroll: () => {
      if (isMobile || isTablet) {
        const scrollY = document.body.dataset.scrollY;
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.width = '';
        document.body.style.overflow = '';
        if (scrollY) {
          window.scrollTo(0, parseInt(scrollY, 10));
        }
        delete document.body.dataset.scrollY;
      }
    },
    
    // Get mobile-optimized classes
    getMobileClasses: (baseClasses = '', mobileClasses = '', tabletClasses = '', desktopClasses = '') => {
      let classes = baseClasses;
      
      if (isSmallMobile && mobileClasses) {
        classes += ` ${mobileClasses} mobile-xs`;
      } else if (isMobile && mobileClasses) {
        classes += ` ${mobileClasses}`;
      }
      
      if (isTablet && tabletClasses) {
        classes += ` ${tabletClasses}`;
      }
      
      if (isDesktop && desktopClasses) {
        classes += ` ${desktopClasses}`;
      }
      
      return classes.trim();
    },
    
    // Get responsive container classes
    getContainerClasses: () => {
      return 'w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8';
    },
    
    // Get responsive grid classes
    getGridClasses: (cols = { mobile: 1, tablet: 2, desktop: 3 }) => {
      return `grid grid-cols-${cols.mobile} sm:grid-cols-${cols.tablet} lg:grid-cols-${cols.desktop} gap-4 sm:gap-6`;
    },
    
    // Get responsive text classes
    getTextClasses: (size = 'base') => {
      const sizes = {
        xs: 'text-xs sm:text-sm',
        sm: 'text-sm sm:text-base',
        base: 'text-base sm:text-lg',
        lg: 'text-lg sm:text-xl',
        xl: 'text-xl sm:text-2xl',
        '2xl': 'text-2xl sm:text-3xl',
        '3xl': 'text-3xl sm:text-4xl'
      };
      return sizes[size] || sizes.base;
    },
    
    // Get responsive spacing classes
    getSpacingClasses: (type = 'padding', size = 'md') => {
      const prefix = type === 'padding' ? 'p' : 'm';
      const sizes = {
        xs: `${prefix}-2 sm:${prefix}-3`,
        sm: `${prefix}-3 sm:${prefix}-4`,
        md: `${prefix}-4 sm:${prefix}-6`,
        lg: `${prefix}-6 sm:${prefix}-8`,
        xl: `${prefix}-8 sm:${prefix}-12`
      };
      return sizes[size] || sizes.md;
    },
    
    // Check if element is in viewport (mobile optimized)
    isInViewport: (element) => {
      if (!element) return false;
      
      const rect = element.getBoundingClientRect();
      const windowHeight = window.innerHeight;
      
      return (
        rect.top >= 0 &&
        rect.left >= 0 &&
        rect.bottom <= windowHeight &&
        rect.right <= window.innerWidth
      );
    },
    
    // Mobile-friendly touch handling
    handleTouch: (callback, options = {}) => {
      const { preventDefault = true, passive = false } = options;
      
      return (event) => {
        if (preventDefault && touchDevice) {
          event.preventDefault();
        }
        
        if (touchDevice) {
          callback(event);
        }
      };
    },
    
    // Get optimal image sizes for different screens
    getImageSizes: () => {
      if (isSmallMobile) return '(max-width: 374px) 100vw';
      if (isMobile) return '(max-width: 767px) 100vw';
      if (isTablet) return '(max-width: 1023px) 50vw';
      return '33vw';
    },
    
    // Handle safe area insets for mobile devices
    getSafeAreaClasses: () => {
      return isMobile ? 'pb-safe-bottom pt-safe-top' : '';
    }
  };

  return {
    isMobile,
    isTablet,
    isDesktop,
    isSmallMobile,
    isLargeMobile,
    orientation,
    touchDevice,
    screenSize,
    mobileUtils
  };
};

export default useMobileOptimization;
